import React, { useEffect, useMemo, useState, useRef, useCallback, useDeferredValue } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchTransactionRange } from '../store/slices/transactionsSlice';
import { fetchTags, selectTagMap } from '../store/slices/tagsSlice';

import { TagMap, Transaction } from '../types';
import CurrencyDisplay from '../components/AmountDisplay';
import { FiChevronRight } from 'react-icons/fi'; // Back and Fullscreen icons
import CashFlowChart from '../components/CashFlowChart'; // Import the new chart component
import ScreenContainer from '../components/ScreenContainer';
import CashFlowDetailsSkeleton from '../components/skeletons/CashFlowDetailsSkeleton'; // Import skeleton
import { useNavigation } from '../hooks/useNavigation';
import { isInvestmentDebitTransaction } from '../utils/cashFlowUtils';
import { getCurrentKolkataYearMonth, parseTransactionDateParts } from '../utils/dateTimeUtils';

interface CashFlowTotals {
    incoming: number;
    outgoing: number;
    invested: number;
}

const EMPTY_TOTALS: CashFlowTotals = { incoming: 0, outgoing: 0, invested: 0 };

const getMonthKey = (year: number, month: number): string => `${year}-${month}`;

const buildMonthlyTotals = (transactions: Transaction[], tagMap: TagMap): Map<string, CashFlowTotals> => {
    const totalsByMonth = new Map<string, CashFlowTotals>();

    transactions.forEach((tx) => {
        if (tx.excludeFromAccounting) {
            return;
        }

        const txDate = parseTransactionDateParts(tx.transactionDate);
        const monthKey = getMonthKey(txDate.year, txDate.month);
        const monthTotals = totalsByMonth.get(monthKey) ?? { incoming: 0, outgoing: 0, invested: 0 };

        if (tx.type === 'CREDIT') {
            monthTotals.incoming += tx.amount;
        } else if (isInvestmentDebitTransaction(tx, tagMap)) {
            monthTotals.invested += tx.amount;
        } else {
            monthTotals.outgoing += tx.amount;
        }

        totalsByMonth.set(monthKey, monthTotals);
    });

    return totalsByMonth;
};

// Helper function to calculate percentage change
const calculatePercentageChange = (current: number, previous: number): number | null => {
    if (previous === 0) {
        return current === 0 ? 0 : null;
    }

    return ((current - previous) / Math.abs(previous)) * 100;
};

const formatPercentageChange = (change: number | null, hasPreviousMonthData: boolean): string => {
    if (!hasPreviousMonthData) {
        return '--';
    }

    if (change === null) {
        return '--';
    }

    return `${Math.abs(change).toFixed(1)}% ${change >= 0 ? 'more' : 'less'}`;
};

// Helper function to get month name abbreviation
const getMonthAbbr = (month: number) => {
    return new Date(2000, month - 1, 1).toLocaleString('default', { month: 'short' });
}

// Helper function to get full month name
const getMonthName = (year: number, month: number) => {
    return new Date(year, month - 1).toLocaleString('default', { month: 'long' });
}

// New helper functions checking Redux state
const doesMonthDataExistInStore = (
    availableMonths: { [year: string]: { [month: number]: boolean } },
    year: number,
    month: number
): boolean => {
    // Check if year exists and month is true
    return availableMonths[String(year)]?.[month] === true;
};

const CashFlowDetailsScreen: React.FC = () => {
    const dispatch = useAppDispatch();
    const { navigateTo } = useNavigation();
    const { transactions, status, availableMonths } = useAppSelector((state) => state.transactions);
    const { status: tagsStatus } = useAppSelector((state) => state.tags);
    const tagMap = useAppSelector(selectTagMap);
    const deferredTransactions = useDeferredValue(transactions);
    const deferredTagMap = useDeferredValue(tagMap);
    const [showInitialSkeleton, setShowInitialSkeleton] = useState(true);

    // Get initial year/month from location state, fallback to current month/year
    // const initialState = location.state as { year: number; month: number } | undefined;
    const { year: initialYear, month: initialMonth } = getCurrentKolkataYearMonth();

    // Use state to manage the currently *selected* month/year (for data display)
    const [selectedYear, setSelectedYear] = useState<number>(initialYear);
    const [selectedMonth, setSelectedMonth] = useState<number>(initialMonth);

    // Keep the chart window fixed while scrolling changes the selected month.
    const [dataFetchDateRange] = useState(() => {
        const dataFetchMonthsCount = 24;
        const selectedDate = new Date(initialYear, initialMonth - 1, 1);

        // End date is the selected month
        const endDate = new Date(selectedDate);

        // Start date is 23 months before the selected month (total 24 months including end)
        const startDate = new Date(selectedDate);
        startDate.setMonth(startDate.getMonth() - (dataFetchMonthsCount - 1));

        return {
            startYear: startDate.getFullYear(),
            startMonth: startDate.getMonth() + 1, // Convert back to 1-indexed
            endYear: endDate.getFullYear(),
            endMonth: endDate.getMonth() + 1, // Convert back to 1-indexed
        };
    });

    // Calculate previous month based on selected state
    const { prevYear, prevMonth } = useMemo(() => {
        const date = new Date(selectedYear, selectedMonth - 1, 1); // Use state vars
        date.setMonth(date.getMonth() - 1);
        return { prevYear: date.getFullYear(), prevMonth: date.getMonth() + 1 };
    }, [selectedYear, selectedMonth]); // Depend on state vars

    // Track if fetches for the current keys have been initiated
    const fetchInitiatedRef = useRef<{ rangeKey?: string; prevKey?: string; range?: boolean; prev?: boolean }>({});

    // Effect to fetch data for the required range and previous month
    useEffect(() => {
        if (tagsStatus === 'idle') {
            dispatch(fetchTags());
        }
    }, [dispatch, tagsStatus]);

    useEffect(() => {
        const { startYear, startMonth, endYear, endMonth } = dataFetchDateRange;

        // Read current status directly here
        const currentStatus = status; // Get status from closure
        // Note: availableMonths is now selected directly via useAppSelector

        // Check and Fetch Range Data using the new store check
        if (!fetchInitiatedRef.current.range &&
            currentStatus !== 'loading' &&
            currentStatus !== 'loadingMore') {
            console.log(`EFFECT: Dispatching fetchTransactionRange for range: ${startYear}-${startMonth} to ${endYear}-${endMonth}`);
            dispatch(fetchTransactionRange({ startYear, startMonth, endYear, endMonth }));
            fetchInitiatedRef.current.range = true; // Mark as initiated for this range key
        }

        if (fetchInitiatedRef.current.range && status === 'succeeded') {
            setShowInitialSkeleton(false);
        }

        // Removed redundant previous month check

        // Dependencies: Only re-run when the core date definitions change, or if status changes.
        // availableMonths is included so the check re-evaluates when it updates.
    }, [dispatch, dataFetchDateRange, prevYear, prevMonth, status, availableMonths]); // Updated dependencies

    const monthlyTotals = useMemo(
        () => buildMonthlyTotals(deferredTransactions, deferredTagMap),
        [deferredTransactions, deferredTagMap]
    );

    // Prepare data for the chart - use the wider fetch range
    const chartData = useMemo(() => {
        const data: { year: number; month: number; monthAbbr: string; incoming: number; outgoing: number; invested: number }[] = [];
        const { startYear, startMonth, endYear, endMonth } = dataFetchDateRange;

        let current = new Date(startYear, startMonth - 1, 1);
        const end = new Date(endYear, endMonth - 1, 1);

        // 1. Generate data for all available months in the range
        while (current <= end) {
            const year = current.getFullYear();
            const month = current.getMonth() + 1;
            if (doesMonthDataExistInStore(availableMonths, year, month)) {
                const totals = monthlyTotals.get(getMonthKey(year, month)) ?? EMPTY_TOTALS;
                data.push({
                    year: year,
                    month: month,
                    monthAbbr: getMonthAbbr(month),
                    incoming: totals.incoming,
                    outgoing: totals.outgoing,
                    invested: totals.invested,
                });
            }
            current.setMonth(current.getMonth() + 1);
        }

        // 2. Find the index of the first month with any non-zero total
        const firstMonthWithActivityIndex = data.findIndex(d =>
            d.incoming > 0 || d.outgoing > 0 || d.invested > 0
        );


        // 3. If a month with activity is found, slice the array from that index onwards
        if (firstMonthWithActivityIndex !== -1) {
            return data.slice(firstMonthWithActivityIndex);
        } else {
            // 4. Otherwise, return an empty array (or potentially the last month if needed? For now, empty)
            return [];
        }


    }, [monthlyTotals, dataFetchDateRange, availableMonths]); // Depend on indexed totals, and availableMonths/dataFetchDateRange for filtering

    // Calculate totals for the *currently selected* month for display
    const currentMonthTotals = useMemo(() => {
        return monthlyTotals.get(getMonthKey(selectedYear, selectedMonth)) ?? EMPTY_TOTALS;
    }, [monthlyTotals, selectedYear, selectedMonth]);

    const previousMonthTotals = useMemo(() => {
        return monthlyTotals.get(getMonthKey(prevYear, prevMonth)) ?? EMPTY_TOTALS;
    }, [monthlyTotals, prevYear, prevMonth]);

    // Calculate percentage changes
    const incomingChange = calculatePercentageChange(currentMonthTotals.incoming, previousMonthTotals.incoming);
    const investedChange = calculatePercentageChange(currentMonthTotals.invested, previousMonthTotals.invested);
    const outgoingChange = calculatePercentageChange(currentMonthTotals.outgoing, previousMonthTotals.outgoing);
    const hasPreviousMonthData =
        previousMonthTotals.incoming !== 0 ||
        previousMonthTotals.outgoing !== 0 ||
        previousMonthTotals.invested !== 0;

    const isLoading = status === 'loading' || status === 'loadingMore';
    const currentMonthName = getMonthName(selectedYear, selectedMonth);

    // Callback function for the chart to update the selected month/year
    const handleMonthSelect = useCallback((year: number, month: number) => {
        // Avoid state updates if the month/year hasn't actually changed
        if (year !== selectedYear || month !== selectedMonth) {
            console.log(`DetailsScreen: handleMonthSelect called with ${month}/${year}`);
            setSelectedYear(year);
            setSelectedMonth(month);
        }
    }, [selectedYear, selectedMonth]); // Dependencies ensure we have the current state values

    const handleOpenTransactionsScreen = useCallback((type: 'incoming' | 'outgoing' | 'investment') => {
        navigateTo(`/cash-flow/transactions/${type}/${selectedYear}/${selectedMonth}`);
    }, [navigateTo, selectedMonth, selectedYear]);

    // *** ADDED: Top-level loading check ***
    if (showInitialSkeleton || isLoading) {
        return (
            <ScreenContainer title="Cash Flow">
                <CashFlowDetailsSkeleton />
            </ScreenContainer>
        );
    }

    return (
        <ScreenContainer title="Cash Flow">
            <div className=" mb-4">
                {/* Top Section (Month, Percentages, Totals) */}
                <div className="flex flex-col justify-center items-center p-8 pb-0 w-full">
                    <div className="max-w-sm w-full">
                        {/* Month/Year Title - Use state variables */}
                        <h2 className="text-xl font-bold">{currentMonthName} {selectedYear}</h2>
                        {/* Percentage Changes */}
                        <div className="text-xs text-muted-foreground mt-1 w-full flex items-center justify-between gap-2">
                            <span className="flex flex-1 items-center justify-start text-emerald-500 dark:text-emerald-400 whitespace-nowrap">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400 mr-1"></span>
                                {formatPercentageChange(incomingChange, hasPreviousMonthData)}
                            </span>
                            <span className="flex flex-1 items-center justify-start text-[#2C84FF] dark:text-[#4EA3FF] whitespace-nowrap">
                                <span className="w-2 h-2 rounded-full bg-[#2C84FF] dark:bg-[#4EA3FF] mr-1"></span>
                                {formatPercentageChange(investedChange, hasPreviousMonthData)}
                            </span>
                            <span className="flex flex-1 items-center justify-start text-rose-500 dark:text-rose-400 whitespace-nowrap">
                                <span className="w-2 h-2 rounded-full bg-rose-500 dark:bg-rose-400 mr-1"></span>
                                {formatPercentageChange(outgoingChange, hasPreviousMonthData)}
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            {hasPreviousMonthData ? `As compared to ${getMonthName(prevYear, prevMonth)}` : `No data for previous month (${getMonthName(prevYear, prevMonth)})`}
                        </p>
                    </div>
                </div>

                <div className='flex h-[400px] flex-col justify-start pt-4'>

                    {/* Current Month Totals */}
                    <div className="flex flex-row justify-center">
                        <div className="flex justify-center space-x-2 items-center text-center bg-muted border border-border rounded-2xl p-2">
                            <div className="flex flex-col rounded-lg overflow-hidden border border-emerald-500/30">
                                <span className="w-full text-center text-xs block text-emerald-600 dark:text-emerald-300 p-1 px-2 bg-emerald-500/15 dark:bg-emerald-400/20">Incoming</span>
                                <div className="bg-background px-2 py-.5 min-w-[96px] flex items-center justify-center">
                                    <CurrencyDisplay
                                        amount={currentMonthTotals.incoming}
                                        className="font-bold text-md text-emerald-600 dark:text-emerald-300"
                                        valueClassName="text-emerald-600 dark:text-emerald-300"
                                        symbolClassName="text-emerald-600 dark:text-emerald-300"
                                        type="CREDIT"
                                        showFraction={false}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col rounded-lg overflow-hidden border border-[#2C84FF]/30 dark:border-[#4EA3FF]/35">
                                <span className="w-full text-center text-xs block text-[#2C84FF] dark:text-[#4EA3FF] p-1 px-2 bg-[#2C84FF]/10 dark:bg-[#4EA3FF]/20">Invested</span>
                                <div className="bg-background px-2 py-.5 min-w-[96px] flex items-center justify-center">
                                    <CurrencyDisplay
                                        amount={currentMonthTotals.invested}
                                        className="font-bold text-md text-[#2C84FF] dark:text-[#4EA3FF]"
                                        valueClassName="text-[#2C84FF] dark:text-[#4EA3FF]"
                                        symbolClassName="text-[#2C84FF] dark:text-[#4EA3FF]"
                                        showType={false}
                                        showFraction={false}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col rounded-lg overflow-hidden border border-rose-500/30">
                                <span className="w-full text-center text-xs block text-rose-600 dark:text-rose-300 p-1 px-2 bg-rose-500/15 dark:bg-rose-400/20">Outgoing</span>
                                <div className="bg-background px-2 py-.5 min-w-[96px] flex items-center justify-center">
                                    <CurrencyDisplay
                                        amount={currentMonthTotals.outgoing}
                                        className="font-bold text-md text-rose-600 dark:text-rose-300"
                                        valueClassName="text-rose-600 dark:text-rose-300"
                                        symbolClassName="text-rose-600 dark:text-rose-300"
                                        type="DEBIT"
                                        showFraction={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Line Chart Container - Now with Ref and Scroll Handler */}
                    <div
                        className="w-full relative"
                    >
                        {chartData.length > 1 ? (
                            <>
                                <div className="absolute -top-1 h-4 left-1/2 transform -translate-x-1/2 w-4 rounded-full border-4 border-background bg-muted-foreground z-[1] pointer-events-none"></div>
                                {/* Centered Vertical Indicator Line - ADDED HERE */}
                                <div className="absolute top-0 bottom-0 mb-[26px] left-1/2 transform -translate-x-1/2 w-[2px] bg-muted-foreground z-[1] pointer-events-none"></div>
                                <CashFlowChart
                                    data={chartData.map(d => ({ // Map data to expected structure
                                        month: d.monthAbbr, // Use monthAbbr for the 'month' prop
                                        incoming: d.incoming,
                                        outgoing: d.outgoing,
                                        invested: d.invested,
                                        // Include year and numeric month if needed by chart internally
                                        // or for data attributes within the chart component itself
                                        year: d.year,
                                        numericMonth: d.month
                                    }))}
                                    selectedYear={selectedYear}
                                    selectedMonth={selectedMonth}
                                    isLoading={isLoading}
                                    className=""
                                    onMonthSelect={handleMonthSelect}
                                />
                            </>
                        ) : (
                            <div className="h-48 flex items-center justify-center text-muted-foreground w-screen"> {/* Placeholder */}
                                No chart data available for this range.
                            </div>
                        )}
                    </div>

                </div>


            </div>

            {/* Updated Footer Section */}
            <div className=" bg-background p-4 pt-0 space-y-3 flex flex-col items-center">
                {/* Incoming Transactions Card */}
                <button
                    onClick={() => handleOpenTransactionsScreen('incoming')}
                    className="flex justify-between items-center max-w-sm w-full p-3 bg-card rounded-lg border border-border bg-muted hover:bg-muted/80"
                >
                    <div>
                        <p className="text-base font-semibold text-foreground">Incoming Transactions</p>
                        <p className="text-sm text-left text-muted-foreground">{getMonthName(selectedYear, selectedMonth)} {selectedYear}</p>
                    </div>
                    <FiChevronRight size={20} className="text-muted-foreground" />
                </button>

                {/* Outgoing Transactions Card */}
                <button
                    onClick={() => handleOpenTransactionsScreen('outgoing')}
                    className="flex justify-between items-center max-w-sm w-full p-3 bg-card rounded-lg border border-border bg-muted hover:bg-muted/80"

                >
                    <div>
                        <p className="text-base font-semibold text-foreground">Outgoing Transactions</p>
                        <p className="text-sm text-left text-muted-foreground">{getMonthName(selectedYear, selectedMonth)} {selectedYear}</p>
                    </div>
                    <FiChevronRight size={20} className="text-muted-foreground" />
                </button>

                {/* Investments Card */}
                <button
                    onClick={() => handleOpenTransactionsScreen('investment')}
                    className="flex justify-between items-center max-w-sm w-full p-3 bg-card rounded-lg border border-border bg-muted hover:bg-muted/80"
                >
                    <div>
                        <p className="text-base font-semibold text-foreground">Investments</p>
                        <p className="text-sm text-left text-muted-foreground">{getMonthName(selectedYear, selectedMonth)} {selectedYear}</p>
                    </div>
                    <FiChevronRight size={20} className="text-muted-foreground" />
                </button>
            </div>

        </ScreenContainer >
    );
};

export default CashFlowDetailsScreen; 
